<script setup>
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { addContentLogAPI } from '@/api/logServiceApi';
import { fetchBaseServiceAPI } from '@/api/baseServiceApi.js';
import { isObjectEmpty } from '@/composables/functions';
import IconAnalysNav210 from '@/components/svg/IconAnalysNav210.vue';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { calculateDDay } from '@/utils/dateFormatter';
import { DEV_MODE } from '@/config/dev-mode';

const route = useRoute();
const router = useRouter();
const analysisStore = useAnalysisStore();
const dataStore = useAnalysisDataStore();
const { sajuData } = storeToRefs(dataStore);

const props = defineProps({
  guestUserId: {
    required: true,
  },
  yearlyFortuneTicketData: {
    type: Object,
  },
  yearlyFortuneLogData: {
    required: true,
  },
  hasPermission: {
    required: false,
  },
});

const emit = defineEmits(['goPopAnalysis', 'warning']);

const yearlyFortuneLogData = computed(() => {
  if (!props.yearlyFortuneLogData?.length) return null;
  const yearlyFortuneData = props.yearlyFortuneLogData[0];
  return yearlyFortuneData;
});

const dDay = computed(() => calculateDDay(yearlyFortuneLogData.value?.expiryDate, false));

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const formattedCreatedAt = computed(() => formatDate(yearlyFortuneLogData.value?.createdAt));
const formattedExpiryDate = computed(() => formatDate(yearlyFortuneLogData.value?.expiryDate));

const goProduct = () => {
  router.push('/purchase/tickets');
};

const goPopAnalysis = () => {
  // 상세 흐름의 '신년운세 시작하기' 발화 누락 보완 (개요 화면 버튼과 화면 조건 배타적이라 중복 없음)
  addContentLogAPI({
    contentType: props.hasPermission ? 'ayf_fortune_btn_start_paid' : 'ayf_fortune_btn_start_free',
  });
  emit('goPopAnalysis');
};

const isChecking = ref(false);

/** 신년운세 데이터 미리 fetch하여 경고 여부 확인 */
const prefetchAndCheckWarning = async () => {
  if (dataStore.yearlyFortuneWarning) return true;
  if (!isObjectEmpty(dataStore.yearlyFortuneData)) return false;
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return false;

  isChecking.value = true;
  try {
    const guestId = route.params.id;
    const params = {
      guestUserId: guestId,
      lang: 'KOR',
      userName: sajuData.value.inputdata.name ?? '',
      bln: sajuData.value.transdata.lucknum ?? null,
      gender: Number(sajuData.value.inputdata.gender) ?? null,
      unknownTime: sajuData.value.inputdata.unknown_time ?? null,
      analysisTypeId: 9,
      suggestionFlag: props.hasPermission ? '3' : '1',
      year: new Date().getFullYear(),
    };
    const result = await fetchBaseServiceAPI(params);
    const data = result.data || result.rag_data;
    if (data?.['경고']) {
      const message = data['경고']['메시지'] || '운세 분석이 어렵습니다.';
      dataStore.setYearlyFortuneWarning(message);
      return true;
    }
    if (data) {
      dataStore.setYearlyFortuneData(data);
      dataStore.setYearlyFortuneSubData({});
    }
    return false;
  } catch (e) {
    DEV_MODE && console.error('CoverPanelYearlyFortune prefetch 실패:', e);
    return false;
  } finally {
    isChecking.value = false;
  }
};

const clickGoYearlyFortuneButton = async () => {
  addContentLogAPI({
    contentType: props.hasPermission ? 'ayf_fortune_btn_start_paid' : 'ayf_fortune_btn_start_free',
  });

  const hasWarning = await prefetchAndCheckWarning();
  if (hasWarning) {
    emit('warning', dataStore.yearlyFortuneWarning);
    return;
  }

  analysisStore.navigateToContent('/yearly-fortune', 0);
};
</script>

<template>
  <div class="overview-payment-info-wrap">
    <div class="overview-payment-inner">
      <div class="overview-none-wrap" v-if="!hasPermission">
        <div class="overview-none-title">
          <i class="ticket-icon__analysis"></i>
          <span class="tit">보유 신년운세권</span>
          <span class="cnt">{{ yearlyFortuneTicketData?.totalCount || 0 }} 건</span>
        </div>
      </div>

      <!-- 분석권으로 이용 중일 때 -->
      <div class="overview-payment-info-content-box" v-else>
        <div class="overview-payment-info-content">
          <div class="overview-payment-info-title">신년운세를 이용하고 있어요!</div>
          <div class="overview-payment-info-date-box">
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">시작일</div>
              <div class="overview-payment-info-date-value">{{ formattedCreatedAt || '-' }}</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">만료일</div>
              <div class="overview-payment-info-date-value" v-if="yearlyFortuneLogData?.expiryDate">
                {{ formattedExpiryDate }}
              </div>
              <div class="overview-payment-info-date-value" v-else>-</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">남은 기간</div>
              <div
                class="overview-payment-info-date-value red"
                v-if="yearlyFortuneLogData?.expiryDate"
              >
                {{ dDay }} 일 남음
              </div>
              <div class="overview-payment-info-date-value red" v-else>소장용</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 신년운세 소개 -->
      <div class="overview-pay-info-box">
        <div class="yearly-fortune-header-wrapper">
          <div class="tit-box">
            <!-- <IconAnalysNav210 /> -->
            <span>새로운 해를 준비하며<br />나에게 맞는 희망의 방향을 그려봅니다.</span>
          </div>
          <div class="yearly-fortune-info-image">
            <img src="@manseImages/yearly-fortune/img_year_info_01.png" alt="yearly-fortune-info" />
          </div>
        </div>
        <div class="txt-box bg-white">
          <div class="txt">
            2026년은 모두에게 같은 해이지만,<br />그 흐름이 각자에게 닿는 방식은 다릅니다.
          </div>
          <div class="txt">
            이 신년운세는 한 해의 방향 속에서<br />
            내가 어떤 선택에 마음을 실으면 좋을지,<br />
            어디에서 숨을 고르면 좋을지를<br />
            차분히 살펴보는 분석입니다.
          </div>
          <div class="txt">
            <span class="txt-bold">"2026년,"</span> 각자의 자리에서<br />
            작은 희망과 의미 있는 결실이 이어질 수 있기를 바라며,<br />
            그 길을 살펴보는데 낭만만세력이 성실한 마음으로 <br />
            함께하겠습니다.
          </div>
        </div>
      </div>
    </div>

    <div class="overview-btn-box">
      <button
        v-if="analysisStore.selectedMenu === '/overviewYearlyFortune'"
        class="overview-payment-info-btn"
        @click="clickGoYearlyFortuneButton"
      >
        신년운세 시작하기
      </button>
      <div
        v-if="
          analysisStore.selectedMenu.includes('/yearly-fortune') &&
          analysisStore.yearlyFortuneIndex === 2 &&
          !props.hasPermission
        "
        class="overview-payment-info-btn"
        @click="goPopAnalysis"
      >
        신년운세 시작하기
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'src/assets/nangman_style/_analysis_main_color.scss';
.overview-none-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  .overview-none-title {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 20px 28px;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    .tit {
      color: var(--Gray-800, #333);
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
    }
    .cnt {
      color: var(--orange-300-main, #eb4c10);
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 171.429% */
      margin-left: auto;
    }
  }
}
.overview-payment-info-wrap {
  height: 100%;
  width: 100%;
  padding: 28px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow-y: scroll;
}

.overview-pay-info-box {
  display: flex;
  padding: 28px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 12px;
  background: rgba(190, 51, 49, 0.05);
  box-sizing: border-box;
  width: 100%;

  .yearly-fortune-header-wrapper {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    .tit-box {
      margin-left: 12px;
      flex: 1;
      min-width: 0;
      color: #be3331;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 26px;
      display: flex;
      align-items: center;
      gap: 8px;

      span {
        display: inline-flex;
        align-items: center;
        line-height: inherit;
      }
    }
  }
  .yearly-fortune-info-image {
    width: 90px;
    height: 100px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    flex-shrink: 0;
    img {
      max-width: 100%;
      height: auto;
      object-fit: contain;
    }
  }
  .tit-box {
    color: #be3331;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px; /* 155.556% */
    display: flex;
    align-items: center;
    gap: 8px;

    span {
      display: inline-flex;
      align-items: center;
      line-height: inherit;
    }
  }
  .txt-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px; /* 160% */
    &.bg-white {
      display: flex;
      padding: 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
      border-radius: 12px;
      background: var(--gray-100-white, #fff);
    }

    .txt-bold {
      font-weight: 700;
    }
  }
}

.overview-payment-info-content-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 20px;
}
.overview-payment-info {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 28px;
}
.overview-payment-info-content {
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 20px 28px;

  .overview-payment-info-title {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 160% */
    margin-bottom: 8px;
  }

  .overview-payment-info-date-box {
    display: flex;
    width: 100%;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
  }
  .overview-payment-info-date {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
    .overview-payment-info-date-title {
      color: var(--Gray-800, #333);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 171.429% */
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .overview-payment-info-date-value {
      color: var(--Gray-800, #333);
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 171.429% */
      &.red {
        color: var(--orange-300-main, #eb4c10);
      }
    }
  }
}
.overview-btn-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 28px;
}
.overview-pay-info-btn {
  display: flex;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid var(--orange-300-main, #eb4c10);
  background: var(--gray-100-white, #fff);
  color: var(--orange-300-main, #eb4c10);
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
}
.overview-payment-info-btn {
  cursor: pointer;
  display: flex;
  width: 100%;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 133.333% */
}
.overview-payment-info-btn-line {
  cursor: pointer;
  color: var(--orange-300-main, #eb4c10);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px; /* 128.571% */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}
.ticket-icon__analysis {
  background: url('@manseImages/ticket-icon.svg') center center no-repeat;
  background-size: contain;
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

.overview-payment-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
}
</style>
